<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { type Question, TestType } from '@/types/TestReview';
import {
    type QuizPerformance,
    type StudentRatingTiming,
    getDisplayTime,
    type ExamPerformanceTypes,
    type StudyUnitPerformance,
    type PracticePerformance,
    type StagePerformance,
} from '@/types/Performance';
import type { FlightSchoolCourseStudent } from '@/types/FlightSchoolStudent';
import type FlightSchool from '@/types/FlightSchool';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import CoursePerformanceRepository from '@/repositories/CoursePerformanceRepository';
import { formatDateTime } from '@/functions/formatDate';
import { toTitleCase } from '@/functions/toTitleCase';
import { titleGenerators, getStudyUnitNumber } from '@/functions/testReviewHelpers';

const props = defineProps<{
    questions: Question[],
    testType: TestType,
    school: FlightSchool | null,
    course: FlightSchoolCourse | null,
    student: FlightSchoolCourseStudent | null,
    exam: ExamPerformanceTypes,
    isVisible: boolean,
}>();

defineEmits<{ 'update:visibility': [value: boolean], 'transition:end': [], 'transition:start': [] }>();

type IterationGenerator = (exam: ExamPerformanceTypes) => number;

const iterationGenerator: Record<TestType, IterationGenerator> = {
    [TestType.Final]: (finalQuiz: ExamPerformanceTypes) => (finalQuiz as StudyUnitPerformance).studyUnitNumber,
    [TestType.Optional]: (optionalQuiz: ExamPerformanceTypes) => (optionalQuiz as StudyUnitPerformance).studyUnitNumber,
    [TestType.Practice]: (practiceTest: ExamPerformanceTypes) => (practiceTest as PracticePerformance).number,
    [TestType.Stage]: (stageTest: ExamPerformanceTypes) => (stageTest as StagePerformance).stageNumber,
    [TestType.Eoc]: () => 0,
    [TestType.Study]:
        (studyQuestions: ExamPerformanceTypes) => (studyQuestions as StudyUnitPerformance).studyUnitNumber,
};

type ExamDetailsGenerator = (exam: ExamPerformanceTypes) => QuizPerformance;
const examDetailsGenerator: Record<TestType, ExamDetailsGenerator> = {
    [TestType.Final]: (finalQuiz: ExamPerformanceTypes) => (finalQuiz as StudyUnitPerformance).finalQuizPerformance,
    [TestType.Optional]: (optionalQuiz: ExamPerformanceTypes) => (
        (optionalQuiz as StudyUnitPerformance).optionalQuizPerformance
    ),
    [TestType.Stage]: (stageTest: ExamPerformanceTypes) => stageTest as QuizPerformance,
    [TestType.Eoc]: (eocTest: ExamPerformanceTypes) => eocTest as QuizPerformance,
    [TestType.Practice]: (practiceTest: ExamPerformanceTypes) => practiceTest as QuizPerformance,
    [TestType.Study]: (studyQuestions: ExamPerformanceTypes) => studyQuestions as QuizPerformance,
};

const examDetails = computed(() => {
    const getDetails = examDetailsGenerator[props.testType];
    const details = getDetails(props.exam);

    return {
        score: details.score,
        status: details.status,
        startedDate: details.startedDate,
        completedDate: details.completedDate,
    };
});

const testTitle = titleGenerators[props.testType](props.exam);
const studyUnitNumber = getStudyUnitNumber(props.exam);
const isPerformanceShowing = ref<boolean>(props.isVisible);
const timing = ref<StudentRatingTiming | null>(null);
const minPassingRatingTestGrade = import.meta.env.VITE_COURSE_RATING_TEST_PASSING_SCORE as number;
const defaultMinPassingGrade = import.meta.env.VITE_DEFAULT_COURSE_PASSING_SCORE as number;
const totalTime = computed(() => {
    if (timing.value === null) {
        return '00:00:00';
    }

    return getDisplayTime(timing.value.getElementTime(props.testType, iterationGenerator[props.testType](props.exam)));
});

const getPassingGrade = (testType: TestType): number => {
    switch (testType) {
    case TestType.Eoc:
    case TestType.Practice:
        return minPassingRatingTestGrade;
    default:
        return defaultMinPassingGrade;
    }
};

const circularProgressColor = computed(() => {
    if (examDetails.value.score === undefined) {
        return 'badge-gray';
    }

    if (examDetails.value.score >= getPassingGrade(props.testType)) {
        return 'fsum-green';
    }

    return 'dark-red';
});

const totalCorrect = computed(() => props.questions.filter((question) => question.isCorrect).length);
const totalNotAnswered = computed(() => props.questions.filter((question) => question.selected === null).length);

onMounted(async () => {
    if (!props.school || !props.course || !props.student) {
        return;
    }

    timing.value = await CoursePerformanceRepository.getStudentRatingTiming(
        props.school,
        props.course,
        props.student,
    );
});
</script>

<template>
    <div
        class="performance q-px-md q-py-sm"
    >
        <div
            class="container performance-body"
            :class="isPerformanceShowing ? 'open' : 'closed'"
            @transitionend="$emit('transition:end')"
            @transitionstart="isPerformanceShowing ? $emit('transition:start') : null"
        >
            <div class="row q-pt-xs">
                <div class="col-fit performance-circle">
                    <q-circular-progress
                        rounded
                        :value="examDetails.score ?? 0"
                        size="75px"
                        :thickness="0.22"
                        :color="circularProgressColor"
                        show-value
                        track-color="grey-3"
                        class="q-mr-lg q-mb-md"
                        :min="0"
                        :max="100"
                    >
                        {{ examDetails.score !== undefined ? `${ examDetails.score }%` : '--' }}
                    </q-circular-progress>
                </div>
                <div class="col stats q-gutter-y-sm">
                    <div class="correct">
                        <div class="label">
                            Correct
                        </div>
                        <div class="stat-value">
                            <span v-if="questions.length > 0">
                                {{ totalCorrect }} / {{ questions.length }}
                            </span>
                            <span v-else>#/#</span>
                        </div>
                    </div>
                    <div class="incorrect">
                        <div class="label">
                            Incorrect
                        </div>
                        <div class="stat-value">
                            <span v-if="questions.length > 0">
                                {{ questions.length - totalCorrect - totalNotAnswered }} / {{ questions.length }}
                            </span>
                            <span v-else>#/#</span>
                        </div>
                    </div>
                </div>
                <div class="col stats q-gutter-y-sm">
                    <div class="status">
                        <div class="label">
                            Status
                        </div>
                        <div class="stat-value">
                            {{ toTitleCase(examDetails.status) }}
                        </div>
                    </div>
                    <div class="total-time">
                        <div class="label">
                            Total Time
                        </div>
                        <div class="stat-value">
                            {{ totalTime }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="row stats q-pt-md q-gutter-y-sm">
                <div class="col-sm-12 col-6 started">
                    <div class="label">
                        Started
                    </div>
                    <div class="stat-value date">
                        {{ examDetails.startedDate ? formatDateTime(examDetails.startedDate) : 'N/A' }}
                    </div>
                </div>
                <div class="col-sm-12 col-6 ended">
                    <div class="label">
                        Ended
                    </div>
                    <div class="stat-value date">
                        {{ examDetails.completedDate ? formatDateTime(examDetails.completedDate) : 'N/A' }}
                    </div>
                </div>
                <div
                    v-if="studyUnitNumber && $q.screen.xs"
                    class="col-6 study-unit"
                >
                    <div class="label">
                        Study Unit
                    </div>
                    <div class="stat-value">
                        {{ studyUnitNumber }}
                    </div>
                </div>
                <div
                    v-if="$q.screen.xs"
                    class="col-6 exam-type"
                >
                    <div class="label">
                        Exam Type
                    </div>
                    <div class="stat-value">
                        {{ testTitle }}
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="$q.screen.gt.xs"
            class="text-center"
        >
            <q-btn
                :label="isPerformanceShowing ? 'Hide' : 'Show Performance'"
                :icon-right="isPerformanceShowing ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
                class="anchor-button text-body2 q-pt-sm show-hide-button"
                color="fsum-med-blue"
                :ripple="false"
                no-caps
                dense
                flat
                @click="isPerformanceShowing = !isPerformanceShowing; $emit('update:visibility', isPerformanceShowing)"
            />
        </div>
    </div>
    <q-separator
        v-if="$q.screen.gt.xs"
        class="q-px-none"
    />
</template>

<style scoped lang="scss">
.label {
    font-size: 12px;
    color: var(--fsum-dark-blue2);
    font-weight: 700;
    padding-bottom: 5px;
}
.stat-value {
    font-size: 14px;

    &.date {
        white-space: pre;
    }
}
:deep(.q-circular-progress__text) {
    font-size: 14px;
}
.performance-body {
    overflow-y: hidden;
    /** arbitrary height that just needs to be higher than the element's */
    max-height: 700px;
    transition: max-height 0.25s ease-in-out;

    &.closed {
        max-height: 0;
    }
}
</style>
